<template>
  <div class="profile-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>

    <div class="profile-container">
      <div class="page-header">
        <h1>User Profile</h1>
        <p class="subtitle">Manage your account settings</p>
      </div>

      <!-- Tabs -->
      <div class="tabs-wrapper">
        <div class="tabs">
          <button
            :class="['tab', { active: activeTab === 'profile' }]"
            @click="activeTab = 'profile'"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Profile
          </button>
          <button
            :class="['tab', { active: activeTab === 'security' }]"
            @click="activeTab = 'security'"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Reset Password
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading profile...</p>
      </div>

      <!-- Success Message -->
      <Transition name="fade">
        <div v-if="resetPasswordSuccess" class="message success-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span>{{ resetPasswordSuccess }}</span>
        </div>
      </Transition>

      <!-- Error Message -->
      <Transition name="fade">
        <div v-if="error" class="message error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>{{ error }}</span>
        </div>
      </Transition>

      <!-- Profile Tab -->
      <div v-if="user && activeTab === 'profile'" class="content-card">
        <div class="profile-header">
          <div class="profile-avatar">
            {{ userInitials }}
          </div>
          <div class="profile-name">
            <h2>{{ fullName }}</h2>
            <p>{{ user.email }}</p>
          </div>
        </div>

        <div class="profile-details">
          <div class="detail-section">
            <h3 class="section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Account Information
            </h3>
            
            <div class="info-grid">
              <div class="info-item">
                <label>Full Name</label>
                <div class="value">{{ fullName }}</div>
              </div>

              <div class="info-item">
                <label>Email</label>
                <div class="value">
                  <span>{{ user.email }}</span>
                  <span :class="['badge', user.isEmailVerified ? 'badge-success' : 'badge-warning']">
                    {{ user.isEmailVerified ? 'Verified' : 'Unverified' }}
                  </span>
                </div>
              </div>

              <div class="info-item">
                <label>Phone</label>
                <div class="value">
                  <span class="phone-text">
                    {{ user.mobilePhone || 'Not provided' }}
                    <span v-if="user.mobilePhone" :class="['badge', user.isPhoneVerified ? 'badge-success' : 'badge-warning']">
                      {{ user.isPhoneVerified ? 'Verified' : 'Unverified' }}
                    </span>
                  </span>
                  <button 
                    v-if="!user.mobilePhone" 
                    @click="showAddPhoneModal = true"
                    class="btn btn-small btn-primary"
                    :disabled="addingPhone"
                  >
                    {{ addingPhone ? 'Adding...' : 'Add Phone' }}
                  </button>
                  <button 
                    v-else-if="user.mobilePhone && !user.isPhoneVerified" 
                    @click="startPhoneVerification"
                    class="btn btn-small btn-primary"
                    :disabled="sendingCode"
                  >
                    {{ sendingCode ? 'Sending...' : 'Verify' }}
                  </button>
                </div>
                <div id="recaptcha-container"></div>
              </div>

              <div class="info-item">
                <label>Account Status</label>
                <div class="value">
                  <span :class="['badge', user.isActive ? 'badge-success' : 'badge-error']">
                    {{ user.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>

              <div class="info-item">
                <label>Member Since</label>
                <div class="value timestamp">{{ formattedCreatedAt }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <button class="btn btn-danger" @click="handleLogout" :disabled="loggingOut">
            <span v-if="loggingOut" class="spinner"></span>
            <template v-else>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Logout
            </template>
          </button>
        </div>
      </div>

      <!-- Security Tab -->
      <div v-if="user && activeTab === 'security'" class="content-card">
        <div class="detail-section">
          <h3 class="section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Reset Password
          </h3>
          <p class="section-description">Update your password to keep your account secure</p>
          
          <form @submit.prevent="handleResetPassword" class="password-form">
            <div class="form-group">
              <label for="password">New Password</label>
              <div class="input-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="passwordForm.password"
                  required
                  minlength="9"
                  maxlength="50"
                  :disabled="resettingPassword"
                  placeholder="Enter new password"
                  class="form-input with-icon"
                />
                <button 
                  type="button" 
                  class="toggle-password-btn" 
                  @click="showPassword = !showPassword"
                  :disabled="resettingPassword"
                >
                  <svg v-if="showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
              <ul class="password-requirements">
                <li>9-50 characters</li>
                <li>At least one letter</li>
                <li>At least one digit</li>
                <li>At least one symbol (!@#$%^&amp;*(),.?&quot;:{}|&lt;&gt;)</li>
              </ul>
            </div>

            <div class="form-group">
              <label for="passwordConfirm">Confirm Password</label>
              <div class="input-wrapper">
                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <input
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  id="passwordConfirm"
                  v-model="passwordForm.passwordConfirm"
                  required
                  minlength="9"
                  maxlength="50"
                  :disabled="resettingPassword"
                  placeholder="Confirm new password"
                  :class="['form-input', 'with-icon', { 'invalid': passwordsMatch === false }]"
                />
                <button 
                  type="button" 
                  class="toggle-password-btn" 
                  @click="showPasswordConfirm = !showPasswordConfirm"
                  :disabled="resettingPassword"
                >
                  <svg v-if="showPasswordConfirm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              </div>
              <div v-if="passwordsMatch === false" class="validation-error">Passwords do not match</div>
              <div v-if="passwordsMatch === true" class="validation-success">Passwords match</div>
            </div>

            <button type="submit" class="btn btn-primary btn-full" :disabled="resettingPassword || !canSubmitPassword">
              <span v-if="resettingPassword" class="spinner"></span>
              <template v-else>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                </svg>
                Reset Password
              </template>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- Add Phone Modal -->
    <Transition name="modal">
      <div v-if="showAddPhoneModal" class="modal-overlay" @click="closeAddPhoneModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Add Phone Number</h3>
            <button class="close-button" @click="closeAddPhoneModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <p class="modal-description">Enter your phone number to enable verification</p>
            <div class="form-group">
              <div class="phone-input-group">
                <select v-model="phoneForm.countryCode" class="form-select country-select">
                  <option v-for="country in countryCodes" :key="country.code" :value="country.code">
                    {{ country.flag }} {{ country.code }}
                  </option>
                </select>
                <input
                  type="tel"
                  v-model="phoneForm.mobilePhone"
                  placeholder="Phone number"
                  class="form-input phone-input"
                  :disabled="addingPhone"
                />
              </div>
            </div>
            <Transition name="fade">
              <div v-if="addPhoneError" class="message error-message small">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>{{ addPhoneError }}</span>
              </div>
            </Transition>
            <div class="modal-actions">
              <button @click="closeAddPhoneModal" class="btn btn-secondary" :disabled="addingPhone">
                Cancel
              </button>
              <button 
                @click="handleAddPhone" 
                class="btn btn-primary"
                :disabled="addingPhone || !phoneForm.mobilePhone.trim()"
              >
                <span v-if="addingPhone" class="spinner"></span>
                <span v-else>Add Phone</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Phone Verification Modal -->
    <Transition name="modal">
      <div v-if="showVerificationModal" class="modal-overlay" @click="closeVerificationModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Verify Your Phone</h3>
            <button class="close-button" @click="closeVerificationModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <p class="modal-description">Enter the verification code sent to {{ user.mobilePhone }}</p>
            <div class="form-group">
              <input
                type="text"
                v-model="verificationCode"
                placeholder="000000"
                maxlength="6"
                class="form-input verification-input"
                :disabled="verifyingCode"
              />
            </div>
            <Transition name="fade">
              <div v-if="verificationError" class="message error-message small">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>{{ verificationError }}</span>
              </div>
            </Transition>
            <div class="modal-actions">
              <button @click="closeVerificationModal" class="btn btn-secondary" :disabled="verifyingCode">
                Cancel
              </button>
              <button 
                @click="verifyCode" 
                class="btn btn-primary"
                :disabled="verifyingCode || verificationCode.length !== 6"
              >
                <span v-if="verifyingCode" class="spinner"></span>
                <span v-else>Verify</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { api, getCurrentUser, clearCurrentUserCache } from '../utils/api'
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from '../utils/firebase'
import { countryPhoneCodes } from '../utils/countryPhoneCodes'

export default {
  name: 'ProfileView',
  inject: ['env'],
  data() {
    return {
      user: null,
      loading: true,
      error: null,
      loggingOut: false,
      resettingPassword: false,
      resetPasswordSuccess: null,
      passwordForm: {
        password: '',
        passwordConfirm: ''
      },
      activeTab: 'profile',
      passwordsMatch: null,
      showPassword: false,
      showPasswordConfirm: false,
      // Phone verification
      showVerificationModal: false,
      verificationCode: '',
      verificationError: null,
      sendingCode: false,
      verifyingCode: false,
      confirmationResult: null,
      recaptchaVerifier: null,
      // Add phone
      showAddPhoneModal: false,
      addingPhone: false,
      addPhoneError: null,
      phoneForm: {
        mobilePhone: '',
        countryCode: '+1'
      },
      countryCodes: countryPhoneCodes
    }
  },
  computed: {
    userInitials() {
      if (!this.user?.firstName || !this.user?.lastName) return '?'
      return (this.user.firstName[0] + this.user.lastName[0]).toUpperCase()
    },
    fullName() {
      if (!this.user) return ''
      return `${this.user.firstName} ${this.user.lastName}`
    },
    formattedCreatedAt() {
      if (!this.user?.createdAt) return ''
      return new Date(this.user.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    isPasswordValid() {
      const password = this.passwordForm.password
      if (password.length < 9 || password.length > 50) return false
      if (!/[a-zA-Z]/.test(password)) return false
      if (!/\d/.test(password)) return false
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return false
      return true
    },
    canSubmitPassword() {
      return this.isPasswordValid && this.passwordsMatch === true
    }
  },
  async created() {
    await this.fetchUserInfo()
  },
  watch: {
    'passwordForm.password'() {
      this.checkPasswordMatch()
    },
    'passwordForm.passwordConfirm'() {
      this.checkPasswordMatch()
    }
  },
  methods: {
    checkPasswordMatch() {
      if (!this.passwordForm.passwordConfirm) {
        this.passwordsMatch = null
        return
      }
      this.passwordsMatch = this.passwordForm.password === this.passwordForm.passwordConfirm
    },
    async fetchUserInfo() {
      try {
        this.user = await getCurrentUser(this.$router)
      } catch (error) {
        this.error = error.code || 'Something went wrong please contact support'
      } finally {
        this.loading = false
      }
    },
    validatePassword(password) {
      if (password.length < 9 || password.length > 50) {
        return 'Password must be between 9 and 50 characters.'
      }
      if (!/[a-zA-Z]/.test(password)) {
        return 'Password must contain at least one letter.'
      }
      if (!/\d/.test(password)) {
        return 'Password must contain at least one digit.'
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        return 'Password must contain at least one symbol.'
      }
      return null
    },
    async handleResetPassword() {
      const validationError = this.validatePassword(this.passwordForm.password)
      if (validationError) {
        this.error = validationError
        return
      }

      if (this.passwordForm.password !== this.passwordForm.passwordConfirm) {
        this.error = 'Passwords do not match.'
        return
      }

      this.resettingPassword = true
      this.error = null
      this.resetPasswordSuccess = null
      try {
        await api('/users/reset-password', {
          method: 'POST',
          body: JSON.stringify({
            password: this.passwordForm.password,
            passwordConfirm: this.passwordForm.passwordConfirm
          })
        }, this.$router)
        this.passwordForm.password = ''
        this.passwordForm.passwordConfirm = ''
        this.passwordsMatch = null
        try { localStorage.removeItem('fic') } catch (_) { /* ignore storage errors */ }
        this.$router.push('/check-email')
      } catch (error) {
        this.error = error.code || 'Something went wrong please contact support'
      } finally {
        this.resettingPassword = false
      }
    },
    async handleLogout() {
      this.loggingOut = true
      this.error = null
      try {
        await api('/logout', { method: 'POST' }, this.$router)
        try { localStorage.removeItem('fic') } catch (_) { /* ignore storage errors */ }
        clearCurrentUserCache()
        this.$router.push('/login')
      } catch (error) {
        this.error = error.code || 'Something went wrong please contact support'
      } finally {
        this.loggingOut = false
      }
    },
    async startPhoneVerification() {
      this.sendingCode = true
      this.error = null
      this.verificationError = null

      try {
        if (!this.recaptchaVerifier) {
          this.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
            callback: () => {}
          })
        }

        const phoneNumber = this.user.mobilePhone
        const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
        
        this.confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, this.recaptchaVerifier)
        this.showVerificationModal = true
      } catch (error) {
        console.error('Error sending verification code:', error)
        this.error = error.message || 'Failed to send verification code. Please try again.'
        
        if (this.recaptchaVerifier) {
          this.recaptchaVerifier.clear()
          this.recaptchaVerifier = null
        }
      } finally {
        this.sendingCode = false
      }
    },
    async verifyCode() {
      this.verifyingCode = true
      this.verificationError = null

      try {
        const result = await this.confirmationResult.confirm(this.verificationCode)
        const idToken = await result.user.getIdToken()
        
        await api('/users/verify-phone', {
          method: 'POST',
          body: JSON.stringify({
            firebaseIdToken: idToken
          })
        }, this.$router)

        this.user.isPhoneVerified = true
        this.showVerificationModal = false
        this.verificationCode = ''
        this.error = null
        
        await this.fetchUserInfo()
      } catch (error) {
        console.error('Error verifying code:', error)
        this.verificationError = error.message || 'Invalid verification code. Please try again.'
      } finally {
        this.verifyingCode = false
      }
    },
    closeVerificationModal() {
      this.showVerificationModal = false
      this.verificationCode = ''
      this.verificationError = null
    },
    closeAddPhoneModal() {
      this.showAddPhoneModal = false
      this.phoneForm.mobilePhone = ''
      this.phoneForm.countryCode = '+1'
      this.addPhoneError = null
    },
    async handleAddPhone() {
      if (!this.phoneForm.mobilePhone.trim()) {
        this.addPhoneError = 'Please enter a phone number'
        return
      }

      this.addingPhone = true
      this.addPhoneError = null

      try {
        const fullPhoneNumber = this.phoneForm.countryCode + this.phoneForm.mobilePhone.trim()
        
        await api('/users/me/phone', {
          method: 'PATCH',
          body: JSON.stringify({
            mobilePhone: fullPhoneNumber
          })
        }, this.$router)

        this.user.mobilePhone = fullPhoneNumber
        this.closeAddPhoneModal()
        await this.fetchUserInfo()
        await this.startPhoneVerification()
      } catch (error) {
        console.error('Error adding phone:', error)
        this.addPhoneError = error.code || error.message || 'Failed to add phone number. Please try again.'
      } finally {
        this.addingPhone = false
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  min-height: calc(100vh - 40px);
  position: relative;
  padding: 40px 20px;
}

/* Background elements */
.bg-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(rgba(60, 98, 230, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(60, 98, 230, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(60, 98, 230, 0.25) 0%, transparent 70%);
  top: -100px;
  right: 10%;
}

.profile-container {
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.95rem;
}

/* Tabs */
.tabs-wrapper {
  margin-bottom: 24px;
}

.tabs {
  display: flex;
  gap: 4px;
  background: rgba(24, 24, 25, 0.5);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab svg {
  width: 18px;
  height: 18px;
}

.tab:hover {
  color: var(--text-primary);
}

.tab.active {
  background: rgba(60, 98, 230, 0.15);
  color: var(--accent-color);
}

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-state p {
  margin-top: 16px;
  font-size: 1rem;
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(60, 98, 230, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Messages */
.message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.message.small {
  padding: 12px 14px;
  font-size: 0.85rem;
  margin-bottom: 16px;
}

.message.small svg {
  width: 16px;
  height: 16px;
}

.success-message {
  background: rgba(94, 194, 105, 0.1);
  border: 1px solid rgba(94, 194, 105, 0.2);
  color: var(--success-color);
}

.error-message {
  background: rgba(221, 82, 76, 0.1);
  border: 1px solid rgba(221, 82, 76, 0.2);
  color: var(--error-color);
}

/* Content Card */
.content-card {
  background: rgba(24, 24, 25, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

/* Profile header */
.profile-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(60, 98, 230, 0.03);
}

.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 600;
  flex-shrink: 0;
}

.profile-name h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.profile-name p {
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
}

/* Profile details */
.profile-details {
  padding: 24px 28px;
}

.detail-section {
  width: 100%;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 20px 0;
}

.section-title svg {
  width: 18px;
  height: 18px;
  color: var(--accent-color);
}

.section-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: -12px 0 24px 0;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.info-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.info-item:last-child {
  border-bottom: none;
}

.info-item label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-secondary);
  flex-shrink: 0;
  min-width: 120px;
}

.info-item .value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  color: var(--text-primary);
  font-size: 0.95rem;
  text-align: right;
}

.info-item .value.timestamp {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.phone-text {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Badges */
.badge {
  display: inline-block;
  font-size: 0.7rem;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge-success {
  background: rgba(94, 194, 105, 0.15);
  color: var(--success-color);
}

.badge-warning {
  background: rgba(227, 157, 59, 0.15);
  color: var(--warning-color);
}

.badge-error {
  background: rgba(221, 82, 76, 0.15);
  color: var(--error-color);
}

/* Card footer */
.card-footer {
  padding: 20px 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: flex-end;
}

/* Password form */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px 28px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  width: 18px;
  height: 18px;
  color: var(--text-muted);
  pointer-events: none;
  transition: color 0.2s ease;
}

.input-wrapper:focus-within .input-icon {
  color: var(--accent-color);
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  background-color: rgba(61, 61, 61, 0.3);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input.with-icon {
  padding-left: 46px;
  padding-right: 46px;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.form-input.invalid {
  border-color: var(--error-color);
}

.form-input.invalid:focus {
  box-shadow: 0 0 0 3px rgba(221, 82, 76, 0.15);
}

.form-select {
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  background-color: rgba(61, 61, 61, 0.3);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn svg {
  width: 18px;
  height: 18px;
}

.toggle-password-btn:hover:not(:disabled) {
  color: var(--text-primary);
}

.toggle-password-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.password-requirements {
  color: var(--text-muted);
  font-size: 0.8rem;
  margin: 4px 0 0 0;
  padding-left: 18px;
}

.password-requirements li {
  margin-bottom: 2px;
}

.validation-error {
  font-size: 0.8rem;
  color: var(--error-color);
}

.validation-success {
  font-size: 0.8rem;
  color: var(--success-color);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;
}

.btn svg {
  width: 18px;
  height: 18px;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-full {
  width: 100%;
}

.btn-small {
  padding: 6px 12px;
  font-size: 0.8rem;
  border-radius: 8px;
}

.btn-small svg {
  width: 14px;
  height: 14px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

.btn-secondary {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.btn-danger {
  background: linear-gradient(135deg, var(--error-color) 0%, #a83232 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(221, 82, 76, 0.25);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(221, 82, 76, 0.35);
}

/* Spinner */
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: rgba(24, 24, 25, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.close-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.2s;
  display: flex;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.close-button:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 20px 0;
  text-align: center;
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.modal-actions .btn {
  flex: 1;
}

/* Phone input */
.phone-input-group {
  display: flex;
  gap: 8px;
}

.country-select {
  flex: 0 0 100px;
  padding: 14px 10px;
}

.phone-input {
  flex: 1;
}

/* Verification input */
.verification-input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5em;
  font-family: 'IBM Plex Mono', monospace;
}

#recaptcha-container {
  position: absolute;
  left: -9999px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 640px) {
  .profile-page {
    padding: 20px 16px;
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }

  .profile-details {
    padding: 20px;
  }

  .password-form {
    padding: 20px;
  }

  .card-footer {
    padding: 16px 20px;
  }

  .card-footer .btn {
    width: 100%;
  }

  .tab {
    padding: 10px 16px;
    font-size: 0.85rem;
  }

  .tab svg {
    width: 16px;
    height: 16px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .info-item label {
    min-width: auto;
  }

  .info-item .value {
    justify-content: flex-start;
    text-align: left;
    width: 100%;
    flex-wrap: wrap;
  }

  .phone-text {
    flex-wrap: wrap;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }
}
</style>
